import { useCallback, useState } from 'react'
import { useDropzone, type FileRejection } from 'react-dropzone'
import { Upload, X, Loader2, AlertCircle, FileText, FileImage, File } from 'lucide-react'
import {
  usePropertyDocuments,
  useUploadDocument,
  useDeleteDocument,
  type PropertyDocument,
  type DocumentFileType,
} from '@/hooks'
import { formatBytes } from '@/lib/imageCompression'

// ============================================================================
// Constants
// ============================================================================

const MAX_FILE_SIZE = 50 * 1024 * 1024 // 50MB

/**
 * Accepted MIME types for document uploads.
 * Includes PDF, Word documents, Excel spreadsheets, and images.
 */
const ACCEPTED_MIME_TYPES = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
}

// ============================================================================
// Helper Functions
// ============================================================================

function getErrorMessage(rejection: FileRejection): string {
  const error = rejection.errors[0]
  if (error.code === 'file-too-large') {
    return `El archivo es muy grande (máx. ${formatBytes(MAX_FILE_SIZE)})`
  }
  if (error.code === 'file-invalid-type') {
    return 'Tipo de archivo inválido. Solo se permiten PDF, DOC, DOCX, XLS, XLSX e imágenes.'
  }
  return error.message
}

/**
 * Get the appropriate icon component for a document file type.
 */
function getDocumentIcon(fileType: DocumentFileType) {
  switch (fileType) {
    case 'pdf':
      return FileText
    case 'doc':
      return FileText
    case 'image':
      return FileImage
    default:
      return File
  }
}

/**
 * Get file type color for badge styling.
 */
function getFileTypeColor(fileType: DocumentFileType): string {
  switch (fileType) {
    case 'pdf':
      return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
    case 'doc':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
    case 'image':
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
    default:
      return 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
  }
}

/**
 * Get file type label in Spanish.
 */
function getFileTypeLabel(fileType: DocumentFileType): string {
  switch (fileType) {
    case 'pdf':
      return 'PDF'
    case 'doc':
      return 'Documento'
    case 'image':
      return 'Imagen'
    default:
      return 'Archivo'
  }
}

// ============================================================================
// Props Interface
// ============================================================================

interface DocumentUploaderProps {
  /** Property ID to upload documents to */
  propertyId: string
  /** Whether the uploader is disabled */
  disabled?: boolean
}

// ============================================================================
// Component
// ============================================================================

export function DocumentUploader({ propertyId, disabled = false }: DocumentUploaderProps) {
  const [uploadError, setUploadError] = useState<string | null>(null)

  // Queries and mutations
  const { data: documents = [], isLoading: isLoadingDocuments } = usePropertyDocuments(propertyId)
  const uploadMutation = useUploadDocument()
  const deleteMutation = useDeleteDocument()

  // Handle file drop
  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      setUploadError(null)

      // Handle rejected files
      if (rejectedFiles.length > 0) {
        const errorMessages = rejectedFiles.map(
          (rejection) => `${rejection.file.name}: ${getErrorMessage(rejection)}`
        )
        setUploadError(errorMessages.join('; '))
        return
      }

      // Upload all accepted files
      for (const file of acceptedFiles) {
        try {
          await uploadMutation.mutateAsync({
            propertyId,
            file,
          })
        } catch (error) {
          setUploadError(
            error instanceof Error ? error.message : 'Error al subir documento'
          )
          break // Stop on first error
        }
      }
    },
    [propertyId, uploadMutation]
  )

  // Configure dropzone
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
  } = useDropzone({
    onDrop,
    accept: ACCEPTED_MIME_TYPES,
    maxSize: MAX_FILE_SIZE,
    disabled: disabled || uploadMutation.isPending,
    multiple: true,
  })

  // Handle delete
  const handleDelete = async (document: PropertyDocument) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este documento?')) return

    try {
      await deleteMutation.mutateAsync({
        id: document.id,
        propertyId,
      })
    } catch (error) {
      setUploadError(
        error instanceof Error ? error.message : 'Error al eliminar documento'
      )
    }
  }

  const isUploading = uploadMutation.isPending
  const isDeleting = deleteMutation.isPending
  const isBusy = isUploading || isDeleting
  const uploadProgress = uploadMutation.progress

  return (
    <section className="rounded-lg border bg-card p-6">
      <h2 className="mb-4 text-lg font-semibold">Documentos</h2>

      {/* Error Display */}
      {uploadError && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            <p className="font-medium">Error de Subida</p>
            <p>{uploadError}</p>
          </div>
          <button
            type="button"
            onClick={() => setUploadError(null)}
            className="ml-auto flex-shrink-0 p-1 hover:opacity-70"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={`
          relative cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors
          ${isDragActive && !isDragReject ? 'border-primary bg-primary/5' : ''}
          ${isDragReject ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : ''}
          ${!isDragActive && !disabled ? 'border-muted-foreground/25 hover:border-muted-foreground/50' : ''}
          ${disabled || isBusy ? 'cursor-not-allowed opacity-50' : ''}
        `}
      >
        <input {...getInputProps()} />

        {isUploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Subiendo...</p>
            {uploadProgress !== null && (
              <div className="w-full max-w-xs">
                <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full bg-primary transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {uploadProgress}% completado
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload className="h-10 w-10 text-muted-foreground" />
            <div>
              <p className="font-medium">
                {isDragActive
                  ? isDragReject
                    ? 'Tipo de archivo inválido'
                    : 'Suelta los documentos aquí'
                  : 'Arrastra y suelta documentos aquí'}
              </p>
              <p className="text-sm text-muted-foreground">
                o haz clic para seleccionar archivos
              </p>
            </div>
            <p className="text-xs text-muted-foreground">
              PDF, DOC, DOCX, XLS, XLSX o imágenes hasta {formatBytes(MAX_FILE_SIZE)}
            </p>
          </div>
        )}
      </div>

      {/* Documents List */}
      {isLoadingDocuments ? (
        <div className="mt-6 flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : documents.length > 0 ? (
        <div className="mt-6">
          <h3 className="mb-3 text-sm font-medium text-muted-foreground">
            Documentos Subidos ({documents.length})
          </h3>
          <div className="space-y-2">
            {documents.map((doc: PropertyDocument) => (
              <DocumentCard
                key={doc.id}
                document={doc}
                onDelete={() => handleDelete(doc)}
                isDeleting={
                  deleteMutation.isPending &&
                  deleteMutation.variables?.id === doc.id
                }
                disabled={disabled || isBusy}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-6 flex flex-col items-center justify-center py-8 text-center">
          <FileText className="h-12 w-12 text-muted-foreground/50" />
          <p className="mt-2 text-sm text-muted-foreground">
            Aún no hay documentos subidos
          </p>
        </div>
      )}
    </section>
  )
}

// ============================================================================
// Document Card Component
// ============================================================================

interface DocumentCardProps {
  document: PropertyDocument
  onDelete: () => void
  isDeleting: boolean
  disabled: boolean
}

function DocumentCard({
  document,
  onDelete,
  isDeleting,
  disabled,
}: DocumentCardProps) {
  const Icon = getDocumentIcon(document.file_type)
  const colorClass = getFileTypeColor(document.file_type)
  const typeLabel = getFileTypeLabel(document.file_type)

  return (
    <div className="group flex items-center gap-3 rounded-lg border bg-muted/50 p-3 transition-colors hover:bg-muted">
      {/* Icon */}
      <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${colorClass}`}>
        <Icon className="h-5 w-5" />
      </div>

      {/* Info */}
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium" title={document.name}>
          {document.name}
        </p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className={`rounded px-1.5 py-0.5 font-medium ${colorClass}`}>
            {typeLabel}
          </span>
          {document.file_size && (
            <span>{formatBytes(document.file_size)}</span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 opacity-0 transition-opacity group-hover:opacity-100">
        {/* Download Link */}
        <a
          href={document.url}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-lg p-2 text-muted-foreground hover:bg-background hover:text-foreground"
          title="Descargar documento"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
        </a>

        {/* Delete Button */}
        <button
          type="button"
          onClick={onDelete}
          disabled={disabled || isDeleting}
          title="Eliminar documento"
          className="rounded-lg p-2 text-red-500 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-red-900/20"
        >
          {isDeleting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <X className="h-4 w-4" />
          )}
        </button>
      </div>
    </div>
  )
}
